// Freshness and presence status for a tracked subject, kept free of any store
// or persona dependency so it can be unit-tested under node (subjectKind.js
// re-exports everything here; import from either).

import {NON_REPORTING_HOURS} from "./fleetStatus.js";

// Server timestamps are UTC but lack a timezone suffix; force UTC parsing.
function lastSeenDate(device) {
    const t = device?.last_message_received ?? device?.last_updated;
    if (!t) return null;
    const s = typeof t === "string" && !t.endsWith("Z") ? `${t}Z` : t;
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
}

export function isOnline(device) {
    const d = lastSeenDate(device);
    if (!d) return false;
    return Date.now() - d.getTime() < 30 * 60 * 1000;
}

// Heard from within the server's non-reporting window. Same 30h cutoff as
// DeviceLiveViewResource::fleetStatus() and fleetStatus.js NON_REPORTING_HOURS,
// so the status badge can never call a tracker Offline that the Offline filter
// chip does not count.
export function isReporting(device) {
    const d = lastSeenDate(device);
    if (!d) return false;
    return Date.now() - d.getTime() < NON_REPORTING_HOURS * 60 * 60 * 1000;
}

// Minutes since the last report (Infinity when unknown), for freshness/trust UI.
export function lastSeenMinutes(device) {
    const d = lastSeenDate(device);
    if (!d) return Infinity;
    return (Date.now() - d.getTime()) / 60000;
}

// Human "time ago" for the last report. Kept short for chips/rows.
export function lastSeenAgo(device) {
    const m = lastSeenMinutes(device);
    if (!Number.isFinite(m)) return "No report yet";
    if (m < 1) return "Just now";
    if (m < 60) return `${Math.floor(m)}m ago`;
    const h = m / 60;
    if (h < 24) return `${Math.floor(h)}h ago`;
    return `${Math.floor(h / 24)}d ago`;
}

// live (< 5m) | stale (5-30m) | offline (>= 30m or unknown). Aligns with
// isOnline()'s 30-minute cutoff so the dot, row and marker never disagree.
export function freshness(device) {
    const m = lastSeenMinutes(device);
    if (!Number.isFinite(m)) return "offline";
    if (m < 5) return "live";
    if (m < 30) return "stale";
    return "offline";
}

// Wording for the freshness dot's tooltip. Deliberately NOT the status label:
// a freshness of "offline" means "no update for 30+ min", which is normal for a
// parked tracker, so the word Offline next to a Parked chip would contradict it.
const FRESHNESS_LABEL = {
    live: "Updated in the last 5 min",
    stale: "No update for 5+ min",
    offline: "No update for 30+ min",
};
export function freshnessLabel(device) {
    return FRESHNESS_LABEL[freshness(device)];
}

// moving | idling | live | offline. fleet_status from the resource is the
// source of truth so the marker, the row and the popup agree. The live map
// keeps it current on each MQTT update (DevicesMap rewrites it from the packet
// instead of deleting it), so it is only absent before the list has loaded;
// then we derive motion from motion_state + speed (gps_speed becomes a
// unitified string after an update, so prefer gps_speed_raw).
export function subjectStatusOf(device) {
    // A member who turned location sharing off outranks freshness/motion: their
    // last fix is frozen, so "moving"/"offline" would both mislead. The map
    // still draws them, greyed, on that frozen point, badged "Sharing paused".
    if (device?.sharing_off) return "sharingOff";
    // An enabled phone whose OS location permission is off (reported by the app)
    // ranks above freshness: show a distinct, fixable "Location off" instead of a
    // misleading "Offline" (signal loss). It also outranks neverReported below,
    // because a phone that has never reported BECAUSE its permission is off
    // deserves the actionable reason rather than the generic one.
    if (device?.location_off) return "locationOff";
    // Never heard from at all, so there is no position and no marker. Must beat
    // the isOnline check below, which would call it "Offline" and imply it used
    // to work - the list row is the only place the user can learn why the map
    // has no pin for it.
    if (device?.never_reported) return "neverReported";
    // Silent past the server's own non-reporting cutoff (30h) is Offline no
    // matter what fleet_status last said. Evaluated on the trust tick, so a row
    // loaded as "parked" flips to Offline on its own without a refetch.
    if (!isReporting(device)) return "offline";
    // The server fleet_status is the source of truth (parity with the Go app's
    // subjectStatusOf). "parked" is stationary-but-PRESENT: the server only says
    // offline at 30h, so a tracker last heard 3h ago is Parked, not Offline.
    // The 30-min isOnline heuristic below must not outrank this - it did, and
    // the list row/popup pill read "Offline" under an "Updated 3h ago" line,
    // contradicting both the Offline filter chip (isNonReporting, 30h) and the
    // marker label (which now shares this 30h cutoff).
    switch (device?.fleet_status) {
        case "moving":
        case "idling":
            // The server only says moving/idling for a packet under 3 min old
            // (DeviceLiveViewResource::fleetStatus), and the live map's sweep
            // parks a quiet mover at the same 3 min. Apply the rule here too so
            // a row keeps aging correctly when that sweep is not running (table
            // view unmounts the map) instead of reading "Moving" until refetch.
            if (lastSeenMinutes(device) > 3) return "live";
            return device.fleet_status;
        case "parked":
            return "live";
        case "offline":
            return "offline";
    }
    // No fleet_status (only before the list has loaded - the live path now
    // rewrites it, see DevicesMap's MQTT watcher): fall back to freshness.
    if (!isOnline(device)) return "offline";
    // Online but no longer FRESH (5-30min since the last fix) reads as Stale
    // (amber), not Live/Moving - a frozen dot must never claim to be live. Uses
    // the same freshness() the dot uses, so the marker, dot and row agree.
    if (freshness(device) === "stale") return "stale";
    if (device?.motion_state) {
        const speed = Number(device?.gps_speed_raw ?? device?.gps_speed ?? 0);
        return Number.isFinite(speed) && speed > 2 ? "moving" : "idling";
    }
    return "live";
}
